import React, { useMemo } from 'react';
import { useQueryClient } from "@tanstack/react-query";
import { YStack, Text, H4, XStack } from "tamagui";
import { Card } from '@constants/Themed';
import { View, Platform, Dimensions } from 'react-native';
import LottieView from "lottie-react-native";
import { LinearGradient } from 'expo-linear-gradient';
import styles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

// ApexCharts for web, alternative for native
let Chart: any = null;
if (Platform.OS === 'web') {
  try {
    Chart = require('react-apexcharts').default;
  } catch (error) {
    console.warn('ApexCharts not available for web');
  }
}

const UsageDonutChart = () => {
  const queryClient = useQueryClient();
  const queryState = queryClient.getQueryState(["statCount"]);
  const companyMembers = (queryClient.getQueryData(["companyMembers"]) as any[]) || [];
  const theme = themeSelector();
  const isFetching = (queryState as any)?.isFetching || false;
  const data = queryState?.data as any;

  // Early return for loading state
  if (isFetching) {
    return (
      <Animatable.View
        animation="fadeIn"
        duration={300}
        style={[
          styles.modernCard,
          theme ? styles.modernBgLight : styles.modernBgDark,
          {
            minWidth: 340,
            minHeight: 320,
            justifyContent: 'center',
            alignItems: 'center',
            gap: 16,
            backgroundColor: theme ? 'rgba(255,255,255,0.95)' : 'rgba(30,30,30,0.95)',
            borderColor: theme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
          }
        ]}
      >
        <LottieView
          style={{ height: 80, width: 80 }}
          source={require("@assets/animations/spin-leaf-loader.json")}
          autoPlay
          loop
        />
        <Text style={[
          styles.modernFieldValue,
          {
            color: theme ? '#64748b' : '#94a3b8',
            textAlign: 'center'
          }
        ]}>
          Loading usage analytics...
        </Text>
      </Animatable.View>
    );
  }

  const chartData = useMemo(() => {
    try {
      if (!data || Object.keys(data).length === 0) {
        console.log('UsageDonutChart: No data available');
        return { series: [], labels: [] };
      }

      const limitMap = {
        data: Number(data.limits?.data_limit || 300) || 300,
        models: Number(data.limits?.data_folder_limit || 3) || 3,
        projects: Number(data.limits?.project_limit || 1) || 1,
        users: Number(data.limits?.max_users || 3) || 3,
        storage: Number(data.limits?.storage_limit || 300) || 300,
        aiCall: Number(data.limits?.ai_call_limit || 100) || 100,
      };

      // Prepare data for ApexCharts format
      const stats = [
        {
          name: "Data Records",
          value: data.data ?? 0,
          color: "#3b82f6", // Modern blue
          limit: limitMap.data
        },
        {
          name: "AI Models",
          value: data.models ?? 0,
          color: "#8b5cf6", // Modern purple
          limit: limitMap.models
        },
        {
          name: "Active Projects",
          value: data.projects ?? 0,
          color: "#06b6d4", // Modern cyan
          limit: limitMap.projects
        },
        {
          name: "Team Members",
          value: Array.isArray(companyMembers) ? companyMembers.length || 1 : 1,
          color: "#10b981", // Modern emerald
          limit: limitMap.users
        },
        {
          name: "Storage (MB)",
          value: data.storage ?? 0,
          color: "#f59e0b", // Modern amber
          limit: limitMap.storage
        },
        {
          name: "AI Calls",
          value: data.aiCall ?? 0,
          color: "#ef4444", // Modern red
          limit: limitMap.aiCall
        },
      ];

      // Filter out items with zero values for a cleaner chart
      const filteredStats = stats.filter(item => item.value > 0);

      // Format for ApexCharts: separate series and labels arrays
      const apexData = {
        series: filteredStats.map(item => item.value),
        labels: filteredStats.map(item => item.name),
        colors: filteredStats.map(item => item.color),
        rawData: filteredStats
      };

      console.log('UsageDonutChart: Generated ApexCharts data', apexData);
      return apexData;
    } catch (error) {
      console.error('UsageDonutChart: Error generating chart data', error);
      return { series: [], labels: [] };
    }
  }, [data, companyMembers]);

  // Remove the old chartConfig for react-native-chart-kit
  const chartOptions = useMemo(() => ({
    chart: {
      type: 'donut' as const,
      height: 300,
      fontFamily: 'inherit',
      toolbar: {
        show: false,
      },
      background: 'transparent',
    },
    colors: (chartData as any).colors || ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'],
    dataLabels: {
      enabled: false,
    },
    legend: {
      position: 'bottom' as const,
      horizontalAlign: 'center' as const,
      fontSize: '14px',
      fontWeight: 500,
      labels: {
        colors: theme ? '#374151' : '#d1d5db',
      },
      markers: {
        width: 12,
        height: 12,
        radius: 6,
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total Usage',
              fontSize: '16px',
              fontWeight: 600,
              color: theme ? '#1e293b' : '#f1f5f9',
            },
          },
        },
      },
    },
    tooltip: {
      enabled: true,
      theme: theme ? 'light' : 'dark',
      y: {
        formatter: function (val: number) {
          return val.toString();
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            height: 250,
          },
          legend: {
            fontSize: '12px',
          },
        },
      },
    ],
  }), [(chartData as any).colors, theme]);

  // Check if chartData has data
  if (!chartData || !chartData.series || chartData.series.length === 0) {
    return (
      <Animatable.View
        animation="fadeIn"
        duration={300}
        style={[
          styles.modernCard,
          theme ? styles.modernBgLight : styles.modernBgDark,
          {
            minWidth: 340,
            minHeight: 320,
            justifyContent: 'center',
            alignItems: 'center',
            gap: 16,
            backgroundColor: theme ? 'rgba(255,255,255,0.95)' : 'rgba(30,30,30,0.95)',
            borderColor: theme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
          }
        ]}
      >
        <View style={[
          styles.modernIconButton,
          {
            width: 80,
            height: 80,
            backgroundColor: theme ? 'rgba(100, 116, 139, 0.1)' : 'rgba(148, 163, 184, 0.2)'
          }
        ]}>
          <LottieView
            style={{ width: 50, height: 50 }}
            source={require("@assets/animations/88006-warning-yellow.json")}
            autoPlay
            loop={false}
          />
        </View>
        <YStack alignItems="center" gap={8}>
          <Text style={[
            styles.modernSectionTitle,
            {
              fontSize: 16,
              textAlign: 'center',
              color: theme ? '#1e293b' : '#f1f5f9'
            }
          ]}>
            No Usage Data
          </Text>
          <Text style={[
            styles.modernFieldValue,
            {
              color: theme ? '#64748b' : '#94a3b8',
              textAlign: 'center',
              fontSize: 14
            }
          ]}>
            Start using SmartSaaS to see your analytics here
          </Text>
        </YStack>
      </Animatable.View>
    );
  }

  // Calculate total usage for percentage display with additional safety
  const safeChartData = (chartData as any)?.rawData || [];
  const totalUsage = safeChartData.length > 0
    ? safeChartData.reduce((sum: number, item: any) => sum + (item?.value || 0), 0)
    : 0;

  return (
    <Animatable.View
      animation="slideInUp"
      duration={400}
      delay={100}
      style={[
        styles.modernCard,
        theme ? styles.modernBgLight : styles.modernBgDark,
        {
          minWidth: 340,
          minHeight: 380,
          overflow: 'hidden',
          backgroundColor: theme ? 'rgba(255,255,255,0.95)' : 'rgba(30,30,30,0.95)',
          borderColor: theme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
        }
      ]}
    >
      {/* Modern Header with Gradient */}
      <LinearGradient
        colors={theme
          ? ['rgba(59, 130, 246, 0.05)', 'rgba(139, 92, 246, 0.05)']
          : ['rgba(59, 130, 246, 0.15)', 'rgba(139, 92, 246, 0.15)']
        }
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 80,
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24
        }}
      />

      <XStack justifyContent="space-between" alignItems="center" marginBottom={20} style={{ zIndex: 1 }}>
        <YStack gap={4}>
          <H4 style={[
            styles.modernSectionTitle,
            {
              fontSize: 18,
              marginBottom: -8,
              color: theme ? '#1e293b' : '#f1f5f9'
            }
          ]}>
            Usage Analytics
          </H4>
          <Text style={[
            styles.modernFieldValue,
            {
              color: theme ? '#64748b' : '#94a3b8',
              fontSize: 12,
              top: -20
            }
          ]}>
            {safeChartData.length} active categories • {totalUsage} total items
          </Text>
        </YStack>
        <View style={[
          styles.modernIconButton,
          {
            backgroundColor: theme
              ? 'rgba(59, 130, 246, 0.1)'
              : 'rgba(59, 130, 246, 0.2)'
          }
        ]}>
          <LottieView
            style={{ width: 24, height: 24 }}
            source={require("@assets/animations/5095-google-form.json")}
            autoPlay
            loop
          />
        </View>
      </XStack>

      {/* Enhanced Chart Container */}
      <View style={{
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme
          ? 'rgba(248, 250, 252, 0.8)'
          : 'rgba(20, 20, 20, 0.8)',
        borderRadius: 20,
        paddingVertical: 20,
        marginTop: 10,
        borderWidth: 1,
        borderColor: theme
          ? 'rgba(0,0,0,0.05)'
          : 'rgba(255,255,255,0.05)',
        ...(Platform.OS === 'web' && {
          backdropFilter: 'blur(10px)',
          boxShadow: theme
            ? 'inset 0 2px 8px rgba(0,0,0,0.06)'
            : 'inset 0 2px 8px rgba(255,255,255,0.06)',
        })
      }}>
        {Platform.OS === 'web' && Chart ? (
          <Chart
            options={chartOptions}
            series={chartData.series}
            type="donut"
            height={300}
            width="100%"
          />
        ) : (
          // Fallback for native - simple text display
          <View style={{ alignItems: 'center', gap: 16 }}>
            <Text style={[
              styles.modernSectionTitle,
              {
                fontSize: 16,
                color: theme ? '#1e293b' : '#f1f5f9'
              }
            ]}>
              Chart View
            </Text>
            <Text style={[
              styles.modernFieldValue,
              {
                color: theme ? '#64748b' : '#94a3b8',
                textAlign: 'center'
              }
            ]}>
              ApexCharts not supported on native platform
            </Text>
            {safeChartData.slice(0, 5).map((item: any, index: number) => (
              <View key={index} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, width: '100%' }}>
                <View style={{
                  width: 16,
                  height: 16,
                  borderRadius: 8,
                  backgroundColor: item.color
                }} />
                <Text style={[
                  styles.modernFieldValue,
                  {
                    flex: 1,
                    color: theme ? '#1e293b' : '#f1f5f9'
                  }
                ]}>
                  {item.name}: {item.value}
                </Text>
              </View>
            ))}
          </View>
        )}
      </View>

      {/* Modern Stats Summary */}
      <XStack flexWrap="wrap" gap={12} marginTop={16} justifyContent="space-between">
        {safeChartData.length > 0 ? safeChartData.slice(0, 3).map((item: any, index: number) => (
          <Animatable.View
            key={item?.name || `chart-item-${index}`}
            animation="fadeInUp"
            delay={200 + (index * 100)}
            style={[
              styles.modernInfoRow,
              {
                flex: 1,
                minWidth: '30%',
                alignItems: 'center',
                backgroundColor: `${item?.color || '#3b82f6'}${theme ? '15' : '25'}`,
                borderWidth: 1,
                borderColor: `${item?.color || '#3b82f6'}${theme ? '30' : '50'}`,
              }
            ]}
          >
            <YStack alignItems="center" gap={2}>
              <Text style={[
                styles.modernFieldLabel,
                {
                  color: item?.color || '#3b82f6',
                  fontSize: 10
                }
              ]}>
                {(item?.name || 'Unknown').toUpperCase()}
              </Text>
              <Text style={[
                styles.modernFieldValue,
                {
                  fontSize: 14,
                  fontWeight: '700',
                  color: theme ? '#1e293b' : '#f1f5f9'
                }
              ]}>
                {item?.value || 0}
              </Text>
            </YStack>
          </Animatable.View>
        )) : null}
      </XStack>
    </Animatable.View>
  );
};

export default UsageDonutChart;
